import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { EnterpriseLayout } from "@/components/layout/EnterpriseLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Activity, Search, Filter, User, Clock, 
  LogIn, UserPlus, Package, CheckCircle, XCircle,
  Building2, DollarSign, ChevronLeft, ChevronRight
} from "lucide-react";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const ACTION_ICONS = {
  user_login: { icon: LogIn, color: "text-blue-600 bg-blue-100" },
  company_registered: { icon: Building2, color: "text-purple-600 bg-purple-100" },
  user_invited: { icon: UserPlus, color: "text-green-600 bg-green-100" },
  order_created: { icon: Package, color: "text-orange-600 bg-orange-100" },
  order_approved: { icon: CheckCircle, color: "text-green-600 bg-green-100" },
  order_rejected: { icon: XCircle, color: "text-red-600 bg-red-100" },
  budget_created: { icon: DollarSign, color: "text-yellow-600 bg-yellow-100" },
  punchout_started: { icon: Package, color: "text-indigo-600 bg-indigo-100" },
  default: { icon: Activity, color: "text-gray-600 bg-gray-100" }
};

const ACTION_LABELS = {
  user_login: "Koneksyon",
  company_registered: "Konpayi Enskri",
  user_invited: "Itilizatè Envite",
  order_created: "Kòmand Kreye",
  order_approved: "Kòmand Apwouve",
  order_rejected: "Kòmand Rejte",
  budget_created: "Bidjè Kreye",
  punchout_started: "Sesyon Punchout"
};

export const EnterpriseActivity = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [actionTypes, setActionTypes] = useState([]);
  const [users, setUsers] = useState([]);
  
  const limit = 20;
  const token = localStorage.getItem('enterprise_token');
  const currentUser = JSON.parse(localStorage.getItem('enterprise_user') || '{}');

  useEffect(() => {
    if (!token) {
      navigate('/enterprise');
      return;
    }
    
    // Check permission
    if (!['admin', 'super_admin'].includes(currentUser.role)) {
      toast.error("Ou pa gen aksè pou wè aktivite yo");
      navigate('/enterprise/dashboard');
      return;
    }
    
    fetchLogs();
    fetchFilters();
  }, [token, navigate, page, actionFilter, userFilter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      let url = `${API}/enterprise/audit-logs?limit=${limit}&skip=${page * limit}`;
      if (actionFilter) url += `&action_type=${actionFilter}`;
      if (userFilter) url += `&user_id=${userFilter}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching logs:", error);
      if (error.response?.status === 403) {
        toast.error("Ou pa gen aksè pou wè aktivite yo");
        navigate('/enterprise/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchFilters = async () => {
    try {
      const [actionsRes, usersRes] = await Promise.all([
        axios.get(`${API}/enterprise/audit-logs/actions`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/enterprise/audit-logs/users`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setActionTypes(actionsRes.data.actions || []);
      setUsers(usersRes.data.users || []);
    } catch (error) {
      console.error("Error fetching filters:", error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-HT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "Kounye a";
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min de sa`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} èdtan de sa`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)} jou de sa`;
    return formatDate(dateString);
  };

  const getActionInfo = (action) => {
    return ACTION_ICONS[action] || ACTION_ICONS.default;
  };

  const getActionLabel = (action) => {
    return ACTION_LABELS[action] || action;
  };

  const totalPages = Math.ceil(total / limit);

  const filteredLogs = logs.filter(log => 
    !searchTerm || 
    log.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.action?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <EnterpriseLayout>
      <div className="space-y-6" data-testid="enterprise-activity">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-[#232F3E]">Aktivite</h1>
          <p className="text-gray-500">Swiv tout aksyon ki fèt nan sistèm nan</p>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-lg">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Chèche..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-activity"
                />
              </div>
              
              <select
                value={actionFilter}
                onChange={(e) => { setActionFilter(e.target.value); setPage(0); }}
                className="border rounded-lg px-3 py-2 min-w-[150px]"
                data-testid="filter-action"
              >
                <option value="">Tout Aksyon</option>
                {actionTypes.map(action => (
                  <option key={action} value={action}>{getActionLabel(action)}</option>
                ))}
              </select>
              
              <select
                value={userFilter}
                onChange={(e) => { setUserFilter(e.target.value); setPage(0); }}
                className="border rounded-lg px-3 py-2 min-w-[150px]"
                data-testid="filter-user"
              >
                <option value="">Tout Itilizatè</option>
                {users.map(user => (
                  <option key={user.user_id} value={user.user_id}>{user.user_name}</option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Activity List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="loading-spinner" />
          </div>
        ) : filteredLogs.length === 0 ? (
          <Card className="border-0 shadow-lg">
            <CardContent className="p-12 text-center">
              <Activity className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-xl font-semibold text-gray-600">Pa gen aktivite</p>
              <p className="text-gray-400">Aktivite yo ap parèt isit la</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-lg">
            <CardContent className="p-0">
              <div className="divide-y">
                {filteredLogs.map((log) => {
                  const actionInfo = getActionInfo(log.action);
                  const ActionIcon = actionInfo.icon;
                  
                  return (
                    <div
                      key={log.id}
                      className="p-4 hover:bg-gray-50 transition-colors"
                      data-testid={`activity-${log.id}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${actionInfo.color}`}>
                          <ActionIcon className="w-5 h-5" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <span className="font-semibold text-[#232F3E]">
                                {log.user_name || "Sistèm"}
                              </span>
                              <span className="text-gray-500 mx-2">•</span>
                              <span className="text-gray-600">
                                {getActionLabel(log.action)}
                              </span>
                            </div>
                            <span className="text-sm text-gray-400 whitespace-nowrap">
                              {formatTimeAgo(log.created_at)}
                            </span>
                          </div>
                          
                          {/* Details */}
                          {log.details && Object.keys(log.details).length > 0 && (
                            <div className="mt-2 text-sm text-gray-500">
                              {log.details.order_number && (
                                <span className="inline-flex items-center gap-1 mr-3">
                                  <Package className="w-4 h-4" />
                                  {log.details.order_number}
                                </span>
                              )}
                              {log.details.company_name && (
                                <span className="inline-flex items-center gap-1 mr-3">
                                  <Building2 className="w-4 h-4" />
                                  {log.details.company_name}
                                </span>
                              )}
                              {log.details.email && (
                                <span className="inline-flex items-center gap-1 mr-3">
                                  <User className="w-4 h-4" />
                                  {log.details.email}
                                </span>
                              )}
                              {log.details.subtotal && (
                                <span className="inline-flex items-center gap-1 mr-3">
                                  <DollarSign className="w-4 h-4" />
                                  ${log.details.subtotal.toLocaleString()}
                                </span>
                              )}
                            </div>
                          )}
                          
                          {/* IP Address */}
                          {log.ip_address && (
                            <p className="text-xs text-gray-400 mt-1">
                              IP: {log.ip_address}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Montre {page * limit + 1} - {Math.min((page + 1) * limit, total)} sou {total}
            </p>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              <span className="text-sm text-gray-600">
                Paj {page + 1} / {totalPages}
              </span>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </EnterpriseLayout>
  );
};

